import { Eye, Pencil, Printer, CopyX } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MarketplaceBadge, StatusBadge, DuplicateBadge } from "@/components/badges";
import { API } from "@/lib/api";

const openPreview = (id) => {
  const token = localStorage.getItem("snd_token");
  window.open(`${API}/labels/${id}/pdf${token ? `?token=${token}` : ""}`, "_blank");
};

export const LabelsTable = ({ labels, selected, onToggle, onToggleAll, onPrintOne, onAssign, onNotDuplicate, offset = 0 }) => {
  const allSelected = labels.length > 0 && labels.every((l) => selected.has(l.id));
  let lastWh = null;
  return (
    <div className="rounded-md border border-slate-800 bg-[#111827]">
      <Table data-testid="labels-table" containerClassName="max-h-[70vh]">
        <TableHeader className="sticky top-0 z-10 bg-[#0F172A] shadow-[0_1px_0_0_#1e293b]">
          <TableRow className="border-slate-800 hover:bg-transparent">
            <TableHead className="w-10"><Checkbox data-testid="labels-table-select-all" checked={allSelected} onCheckedChange={onToggleAll} /></TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">#</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Warehouse</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">SKU (master / platform)</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Product</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Qty</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Marketplace</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Order / Tracking</TableHead>
            <TableHead className="font-mono text-[11px] uppercase tracking-widest">Status</TableHead>
            <TableHead className="text-right font-mono text-[11px] uppercase tracking-widest">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {labels.length === 0 && (
            <TableRow><TableCell colSpan={10} className="py-12 text-center text-slate-500" data-testid="labels-empty">No labels match these filters. Try "All statuses" or upload new label PDFs from the Dashboard.</TableCell></TableRow>
          )}
          {labels.map((l, i) => {
            const whChanged = l.warehouse_id !== lastWh;
            lastWh = l.warehouse_id;
            return (
              <TableRow key={l.id} data-testid={`label-row-${l.id}`}
                className={`row-hover border-slate-800/70 hover:bg-slate-800/40 ${whChanged && i > 0 ? "border-t-2 border-t-blue-900/60" : ""} ${!l.matched ? "bg-red-950/20" : l.duplicate ? "bg-orange-950/20" : ""}`}>
                <TableCell><Checkbox data-testid={`label-select-${l.id}`} checked={selected.has(l.id)} onCheckedChange={() => onToggle(l.id)} /></TableCell>
                <TableCell className="font-mono text-xs text-slate-500">{offset + i + 1}</TableCell>
                <TableCell className="font-mono text-sm font-semibold text-blue-300">{l.warehouse_id || <span className="text-red-400">—</span>}</TableCell>
                <TableCell className="font-mono text-sm">
                  <div className="font-semibold">{l.master_sku || l.sku_id || <span className="text-red-400">not found</span>}</div>
                  {l.sku_id && l.master_sku && l.sku_id.toLowerCase() !== l.master_sku.toLowerCase() && <div className="text-[11px] text-slate-500">{l.sku_id}</div>}
                  {l.items?.length > 1 && <div className="mt-0.5 text-[11px] text-amber-300">+{l.items.length - 1} more item(s): {l.items.slice(1).map((i) => `${i.master_sku || i.sku_id} ×${i.qty}`).join(", ")}</div>}
                  {l.ocr && <span className="font-mono text-[9px] uppercase tracking-widest text-slate-500">ocr</span>}
                </TableCell>
                <TableCell className="max-w-[220px] truncate text-sm text-slate-300">{l.product_name || "—"}</TableCell>
                <TableCell className="font-mono text-sm">{l.qty}</TableCell>
                <TableCell><MarketplaceBadge marketplace={l.marketplace} /></TableCell>
                <TableCell className="font-mono text-xs text-slate-400">
                  <div>{l.order_id || "—"}</div>
                  <div className="text-slate-500">{l.tracking_id || ""}</div>
                </TableCell>
                <TableCell><div className="flex flex-col items-start gap-1"><StatusBadge status={l.status} matched={l.matched} />{l.duplicate && <DuplicateBadge />}</div></TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    {l.duplicate && <Button data-testid={`label-not-duplicate-${l.id}`} size="icon" variant="ghost" className="h-8 w-8 text-orange-300" title="Not a duplicate" onClick={() => onNotDuplicate(l.id)}><CopyX className="h-4 w-4" /></Button>}
                    <Button data-testid={`label-preview-${l.id}`} size="icon" variant="ghost" className="h-8 w-8" title="Preview page" onClick={() => openPreview(l.id)}><Eye className="h-4 w-4" /></Button>
                    <Button data-testid={`label-assign-${l.id}`} size="icon" variant="ghost" className="h-8 w-8" title="Assign SKU/Warehouse" onClick={() => onAssign(l)}><Pencil className="h-4 w-4" /></Button>
                    <Button data-testid={`label-print-${l.id}`} size="icon" variant="ghost" className="h-8 w-8 text-blue-300" title="Print this label" onClick={() => onPrintOne(l.id)}><Printer className="h-4 w-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
